import { z } from 'zod'
import { apiOrders } from '../../../app/redux-fetch/apiOrders'
import { store } from '../../../app/store/store'
import { LoadType } from '../../../types/common'
import { normalizeDate } from '../../utils'
import { containerCollectionSchema } from './schemas/containerCollectionSchema'
import { packagingItemSchema } from './schemas/packagingItemSchema'
import { serviceWaypointSchema } from './schemas/serviceWaypointSchema'
import { transitDetailSchema } from './schemas/transitDetailSchema'

export const schema = z
  .object({
    id: z.number().optional(),
    tenantId: z.number().min(1, { message: 'Tenant is required' }),
    customerId: z.number().min(1, { message: 'Customer is required' }),
    revision: z.number().optional(),
    customerReferenceNumber: z
      .string()
      .max(50, { message: 'Max 50 characters' })
      .nullable()
      .optional(),
    shipmentNumber: z.string().optional(),
    submittedDate: z.date().nullable().optional(),
    statuses: z.array(z.any()).optional(),
    status: z.string().optional().nullable(),
    description: z
      .string()
      .min(1, { message: 'Description is required' })
      .max(200, { message: 'Max 200 characters' }),
    validFrom: z.union([z.date(), z.string()]).nullable().optional(),
    validTo: z.union([z.date(), z.string()]).nullable().optional(),
    bookings: z.array(
      z
        .object({
          id: z.number().optional(),
          bookingId: z.number().optional(),
          clearingRequired: z.boolean().optional(),
          referenceNumber: z.string().max(50, { message: 'Max 50 characters' }).optional(),
          trackingNumber: z.string().optional(),
          loadTypeId: z.number().min(1, { message: 'Load type is required' }),
          shipperId: z
            .number({ message: 'Shipper is required' })
            .min(1, { message: 'Shipper is required' }),
          shipperRevision: z.number().optional(),
          shipper: z.object({}).optional(),
          currencyId: z
            .number({ message: 'Currency is required' })
            .min(1, { message: 'Currency is required' }),
          description: z
            .string()
            .min(1, { message: 'Description is required' })
            .max(200, { message: 'Max 200 characters' }),
          emptyContainerReturn: z
            .object({
              id: z.number().optional(),
              locationId: z
                .number({ message: 'Empty Container Return location is required' })
                .min(1, { message: 'Empty Container Return location is required' }),
              contactId: z.number().optional().nullable(),
              scheduledDate: z
                .date()
                .min(new Date('1900-01-01'), { message: 'Too old' })
                .nullable()
                .optional(),
              location: z.object({}).optional(),
              locationRevision: z.number().optional(),
              specialInstructions: z.string().optional(),
            })
            .optional(),
          returnTo: z
            .object({
              id: z.number().optional(),
              locationId: z
                .number({ message: 'Location is required' })
                .min(1, { message: 'Empty Container Return location is required' }),
              contactId: z.number().optional().nullable(),
              defaultValue: z.boolean().optional(),
              location: z.object({}).optional(),
              locationRevision: z.number().optional(),
              specialInstructions: z.string().optional(),
            })
            .optional(),
          shipFrom: z.object({
            id: z.number().optional(),
            locationId: z
              .number({ message: 'Ship From location is required' })
              .min(1, { message: 'Ship From location is required' }),
            consignmentId: z.number().optional(),
            location: z.object({}).optional(),
            locationRevision: z.number().optional(),
            returnTo: z.boolean().optional(),
            emptyContainerCollection: z.boolean().optional(),
            contactId: z.number().nullable().optional(),
            clearingRequired: z.boolean().optional(),
            defaultValue: z.boolean().optional(),
            specialInstructions: z.string().optional(),
            loadingDate: z.date().min(new Date('1900-01-01'), { message: 'Too old' }).nullable(),
            services: z.array(serviceWaypointSchema).optional(),
            transitDetails: transitDetailSchema,
          }),
          deliverTo: z.object({
            id: z.number().optional(),
            consignmentId: z.number().optional(),
            locationId: z
              .number({ message: 'Deliver To location is required' })
              .min(1, { message: 'Deliver To location is required' }),
            clearingRequired: z.boolean().optional().nullable(),
            location: z.object({}).optional(),
            locationRevision: z.number().optional(),
            contactId: z.number().nullable().optional(),
            deliveryDate: z
              .date()
              .min(new Date('1900-01-01'), { message: 'Too old' })
              .nullable()
              .optional(),
            specialInstructions: z.string().optional(),
            emptyContainerReturn: z.boolean().optional(),
            services: z.array(serviceWaypointSchema).optional(),
            transitDetails: transitDetailSchema.optional(),
          }),
          containerCollections: z.array(containerCollectionSchema).optional(),
          packaging: z.array(packagingItemSchema).optional(),
          waypoints: z
            .array(
              z.object({
                id: z.number().optional(),
                locationId: z
                  .number({ message: 'Waypoint location is required' })
                  .min(1, { message: 'Waypoint location is required' }),
                clearingRequired: z.boolean().optional(),
                loadingDate: z
                  .date()
                  .min(new Date('1900-01-01'), { message: 'Too old' })
                  .nullable()
                  .optional(),
                specialInstructions: z.string().optional(),
                consignmentId: z.number().optional(),
                location: z.object({}).optional(),
                locationRevision: z.number().optional(),
                contactId: z.number().nullable().optional(),
                transitDetails: transitDetailSchema,
                services: z.array(serviceWaypointSchema).optional(),
              })
            )
            .optional(),
        })
        .superRefine((data, context) => {
          const { shipFrom, waypoints, deliverTo, emptyContainerReturn } = data
          const loadingDate = shipFrom?.loadingDate
          const deliveryDate = deliverTo?.deliveryDate
          const emptyContainerReturnDate = emptyContainerReturn?.scheduledDate

          let previousDate: any = null

          if (!loadingDate) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Ship From Scheduled Date is required',
              path: ['shipFrom', 'loadingDate'],
            })
          } else {
            const loadDate = normalizeDate(loadingDate)
            if (previousDate && loadDate <= previousDate) {
              context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Requested Date must be after previous Waypoint Scheduled Date.',
                path: ['shipFrom', 'loadingDate'],
              })
            }
            previousDate = loadDate
          }

          if (deliveryDate) {
            const delDate = normalizeDate(deliveryDate)
            if (previousDate && delDate <= previousDate) {
              context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Requested Date must be after previous Waypoint Scheduled Date.',
                path: ['deliverTo', 'deliveryDate'],
              })
            }
            previousDate = delDate
          }

          if (emptyContainerReturnDate) {
            const ecrDate = normalizeDate(emptyContainerReturnDate)
            if (previousDate && ecrDate <= previousDate) {
              context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Scheduled Date must be after previous Waypoint Scheduled Date.',
                path: ['emptyContainerReturn', 'scheduledDate'],
              })
            }
          }

          const isCrossBorder =
            shipFrom?.transitDetails?.crossBorder === true ||
            deliverTo?.transitDetails?.crossBorder === true ||
            (waypoints || []).some((wp) => wp?.transitDetails?.crossBorder === true)

          const isClearingRequired =
            shipFrom?.clearingRequired === true ||
            deliverTo?.clearingRequired === true ||
            (waypoints || []).some((wp) => wp?.clearingRequired === true)

          if (!isCrossBorder && isClearingRequired) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Clearing Required can only be specified for cross border consignments.',
              path: ['clearingRequired'],
            })
          }

          if (isCrossBorder) {
            if (data.packaging && Array.isArray(data.packaging)) {
              data.packaging.forEach((pack, pIndex) => {
                if (pack.products && Array.isArray(pack.products)) {
                  pack.products.forEach((product, prodIndex) => {
                    if (!product.countryOfOriginId) {
                      context.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Country of Origin is required',
                        path: ['packaging', pIndex, 'products', prodIndex, 'countryOfOriginId'],
                      })
                    }
                  })
                }
              })
            }
          }

          if (data.packaging && Array.isArray(data.packaging)) {
            data.packaging.forEach((pack, pIndex) => {
              const physical = pack.physicalProperties

              if (data.loadTypeId === LoadType.container || data.loadTypeId === LoadType.pallet) {
                if (!pack.packagingTemplateId) {
                  const loadTypeName =
                    data.loadTypeId === LoadType.container ? 'Container' : 'Pallet'
                  context.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `${loadTypeName} Type is required`,
                    path: ['packaging', pIndex, 'packagingTemplateId'],
                  })
                }
              }

              if (data.loadTypeId !== LoadType.parcel) {
                if (physical?.grossWeight === undefined) {
                  context.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Gross Weight is required',
                    path: ['packaging', pIndex, 'physicalProperties', 'grossWeight'],
                  })
                }
              }
              if (pack.temperatureControlled) {
                if (pack.temperatureRange === undefined || pack.temperatureRange === null) {
                  context.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Temperature Range is required',
                    path: ['packaging', pIndex, 'temperatureRange'],
                  })
                }
                if (pack.setPoint === undefined || pack.setPoint === null) {
                  context.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Set Point is required',
                    path: ['packaging', pIndex, 'setPoint'],
                  })
                }
                if (!pack.setPointUnitId) {
                  context.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Set Point Unit is required',
                    path: ['packaging', pIndex, 'setPoint'],
                  })
                }

                if (pack.lowerCritical === undefined || pack.lowerCritical === null) {
                  context.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Lower Critical is required',
                    path: ['packaging', pIndex, 'lowerCritical'],
                  })
                }
                if (pack.upperCritical === undefined || pack.upperCritical === null) {
                  context.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Upper Critical is required',
                    path: ['packaging', pIndex, 'upperCritical'],
                  })
                }

                if (data.loadTypeId === 2) {
                  // Pallet
                  if (physical?.height === undefined || physical?.height === null) {
                    context.addIssue({
                      code: z.ZodIssueCode.custom,
                      message: 'Height is required for pallets',
                      path: ['packaging', pIndex, 'physicalProperties', 'height'],
                    })
                  }
                }
              }
            })
          }

          const validateCarrierForTransitDetails = (
            transitDetails: any,
            path: (string | number)[]
          ) => {
            const ROAD_TRANSPORT_MODE_ID = 3
            const isRoadMode = transitDetails?.modeOfTransit === ROAD_TRANSPORT_MODE_ID

            // Validation for non-Road modes
            if (!isRoadMode) {
              if (
                (transitDetails?.carrierOption === 'preferredCarrier' ||
                  transitDetails?.carrierOption === 'preBookedTransit') &&
                !transitDetails?.carrierId
              ) {
                switch (transitDetails?.carrierOption) {
                  case 'preferredCarrier':
                    context.addIssue({
                      code: z.ZodIssueCode.custom,
                      message: 'Carrier is required',
                      path: [...path, 'transitDetails', 'carrierId'],
                    })
                    break
                  case 'preBookedTransit':
                    context.addIssue({
                      code: z.ZodIssueCode.custom,
                      message: 'Carrier is required when Pre-Booked Transit is selected',
                      path: [...path, 'transitDetails', 'carrierId'],
                    })
                    break
                }
              }

              if (
                transitDetails?.carrierOption === 'preBookedTransit' &&
                (!transitDetails?.transitIdentifier ||
                  transitDetails?.transitIdentifier.trim() === '')
              ) {
                context.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: 'Transit Identifier is required when Pre-Booked Transit is selected',
                  path: [...path, 'transitDetails', 'transitIdentifier'],
                })
              }

              if (
                transitDetails?.carrierOption === 'preBookedTransit' &&
                !transitDetails?.scheduledDate
              ) {
                context.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: 'Scheduled Date is required when Pre-Booked Transit is selected',
                  path: [...path, 'transitDetails', 'scheduledDate'],
                })
              }
            }
          }

          if (shipFrom?.transitDetails) {
            validateCarrierForTransitDetails(shipFrom.transitDetails, ['shipFrom'])
          }

          if (waypoints) {
            waypoints.forEach((waypoint, index) => {
              if (waypoint.transitDetails) {
                validateCarrierForTransitDetails(waypoint.transitDetails, ['waypoints', index])
              }
            })
          }

          if (deliverTo?.transitDetails) {
            validateCarrierForTransitDetails(deliverTo.transitDetails, ['deliverTo'])
          }

          const now = new Date()

          if (shipFrom?.loadingDate) {
            const loadDate = normalizeDate(shipFrom.loadingDate)
            if (loadDate < now) {
              context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Scheduled Date cannot be in the past',
                path: ['shipFrom', 'loadingDate'],
              })
            }
          }

          if (deliverTo?.deliveryDate) {
            const delDate = normalizeDate(deliverTo.deliveryDate)
            if (delDate < now) {
              context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Scheduled Date cannot be in the past',
                path: ['deliverTo', 'deliveryDate'],
              })
            }
          }
          if (emptyContainerReturn?.scheduledDate) {
            const ecrDate = normalizeDate(emptyContainerReturn.scheduledDate)
            if (ecrDate < now) {
              context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Scheduled Date cannot be in the past',
                path: ['emptyContainerReturn', 'scheduledDate'],
              })
            }
          }

          const validateTransitDetailsScheduledDate = (
            transitDetails: any,
            path: (string | number)[],
            waypointIndex?: number
          ) => {
            if (!transitDetails?.scheduledDate) return

            const scheduledDate = normalizeDate(transitDetails.scheduledDate)
            if (scheduledDate < now) {
              context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Scheduled Date cannot be in the past',
                path: [...path, 'transitDetails', 'scheduledDate'],
              })
            }

            if (shipFrom?.loadingDate) {
              const shipFromDate = normalizeDate(shipFrom.loadingDate)
              if (scheduledDate <= shipFromDate) {
                context.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: 'Scheduled Date must be after Ship From Requested Date',
                  path: [...path, 'transitDetails', 'scheduledDate'],
                })
              }
            }

            if (waypoints && waypointIndex !== undefined) {
              const previousWaypoints = waypoints.slice(0, waypointIndex)
              for (let i = 0; i < previousWaypoints.length; i++) {
                const waypoint = previousWaypoints[i]
                if (waypoint.transitDetails?.scheduledDate) {
                  const waypointDate = normalizeDate(waypoint.transitDetails.scheduledDate)
                  if (scheduledDate <= waypointDate) {
                    context.addIssue({
                      code: z.ZodIssueCode.custom,
                      message: 'Scheduled Date must be after previous waypoint Scheduled Date',
                      path: [...path, 'transitDetails', 'scheduledDate'],
                    })
                    break
                  }
                }
              }
            }
          }

          if (shipFrom?.transitDetails) {
            validateTransitDetailsScheduledDate(shipFrom.transitDetails, ['shipFrom'])
          }

          if (waypoints) {
            waypoints.forEach((waypoint, index) => {
              if (waypoint.transitDetails) {
                validateTransitDetailsScheduledDate(
                  waypoint.transitDetails,
                  ['waypoints', index],
                  index
                )
              }
            })
          }

          if (deliverTo?.deliveryDate) {
            const deliveryDate = normalizeDate(deliverTo.deliveryDate)

            if (shipFrom?.loadingDate) {
              const shipFromDate = normalizeDate(shipFrom.loadingDate)
              if (deliveryDate <= shipFromDate) {
                context.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: 'Delivery Date must be after Ship From Requested Date',
                  path: ['deliverTo', 'deliveryDate'],
                })
              }
            }

            if (shipFrom?.transitDetails?.scheduledDate) {
              const shipFromTransitDate = normalizeDate(shipFrom.transitDetails.scheduledDate)
              if (deliveryDate <= shipFromTransitDate) {
                context.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: 'Delivery Date must be after Transit Details Scheduled Date',
                  path: ['deliverTo', 'deliveryDate'],
                })
              }
            }

            if (waypoints && waypoints.length > 0) {
              const lastWaypoint = waypoints[waypoints.length - 1]
              if (lastWaypoint.transitDetails?.scheduledDate) {
                const lastWaypointDate = normalizeDate(lastWaypoint.transitDetails.scheduledDate)
                if (deliveryDate <= lastWaypointDate) {
                  context.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Delivery Date must be after last waypoint Scheduled Date',
                    path: ['deliverTo', 'deliveryDate'],
                  })
                }
              }
            }
          }
        })
    ),
  })
  .superRefine(async (data, ctx) => {
    if (!data.customerReferenceNumber || !data.customerId) return
    const result = await store.dispatch(
      apiOrders.endpoints.getOrderCrn.initiate({
        customerId: data.customerId,
        id: data.id ?? 0,
        crn: data.customerReferenceNumber,
      })
    )
    if ('error' in result || result.data) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Customer Reference Number is already used or invalid',
        path: ['customerReferenceNumber'],
      })
    }
  })
  .superRefine(async (data, ctx) => {
    if (!data.bookings) return

    for (let i = 0; i < data.bookings.length; i++) {
      const booking = data.bookings[i]
      const bookingId = booking.id ?? 0
      const referenceNumber = booking.referenceNumber
      if (referenceNumber) {
        const result = await store.dispatch(
          apiOrders.endpoints.checkCrnExistsForCustomerShipmentBooking.initiate({
            customerId: data.customerId,
            id: bookingId,
            reference: referenceNumber,
          })
        )

        if ('error' in result || result.data) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Customer Reference Number is already used',
            path: ['bookings', i, 'referenceNumber'],
          })
          break
        }
      }
    }
  })
  .superRefine((data, ctx) => {
    if (!data.bookings || data.bookings.length === 0) return

    const crnSet = new Set<string>()
    const duplicateCrns: Array<{ path: (string | number)[]; value: string }> = []

    data.bookings.forEach((booking, index) => {
      if (booking.referenceNumber && booking.referenceNumber.trim() !== '') {
        const crn = booking.referenceNumber.trim()
        if (crnSet.has(crn)) {
          duplicateCrns.push({ path: ['bookings', index, 'referenceNumber'], value: crn })
        } else {
          crnSet.add(crn)
        }
      }
    })

    duplicateCrns.forEach(({ path, value }) => {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Reference Number is not unique across consignments',
        path,
      })
    })
  })

export type IShipmentFormInput = z.infer<typeof schema>
